import { useState, useEffect } from "react";
import type { Period } from "@yarnalytics/shared";
import { ExternalLink, ChevronLeft, ChevronRight, Lock } from "lucide-react";
import { useStore } from "@/lib/store";
import { usePosts } from "@/lib/hooks";
import { PeriodSelector } from "@/components/PeriodSelector";

const LOCKED_PERIODS: Period[] = ["30d", "90d"];

const sortOptions = [
  { value: "recent", label: "Recent" },
  { value: "views", label: "Views" },
  { value: "likes", label: "Likes" },
  { value: "replies", label: "Replies" },
];

function timeAgo(timestamp: number): string {
  const diff = Date.now() / 1000 - timestamp;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(timestamp * 1000).toLocaleDateString();
}

export function PostsPage() {
  const [period, setPeriod] = useState<Period>("7d");
  const [sort, setSort] = useState("recent");
  const [page, setPage] = useState(1);
  const activeAccountId = useStore((s) => s.activeAccountId);
  const isPro = useStore((s) => s.isPro());
  const disabledPeriods = isPro ? [] : LOCKED_PERIODS;

  useEffect(() => {
    if (!isPro && disabledPeriods.includes(period)) {
      setPeriod("7d");
    }
  }, [disabledPeriods, isPro, period]);

  useEffect(() => {
    if (!isPro && sort !== "recent") {
      setSort("recent");
    }
  }, [isPro, sort]);

  const { data, isLoading, isError } = usePosts(
    activeAccountId,
    period,
    sort,
    page
  );


  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold tracking-tight">Posts</h1>
        <PeriodSelector
          value={period}
          onChange={(p) => {
            setPeriod(p);
            setPage(1);
          }}
          disabledValues={disabledPeriods}
        />
      </div>

      {/* Sort */}
      <div className="flex gap-1 mb-4">
        {sortOptions.map((s) => {
          const isLocked = !isPro && s.value !== "recent";

          return (
            <button
              key={s.value}
              onClick={() => {
                if (!isLocked) {
                  setSort(s.value);
                  setPage(1);
                }
              }}
              disabled={isLocked}
              className={`flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                isLocked
                  ? "text-neutral-300 cursor-not-allowed"
                  : sort === s.value
                    ? "bg-black text-white"
                    : "text-neutral-400 hover:text-black"
              }`}
            >
              {s.label}
              {isLocked && <Lock className="h-3 w-3" />}
            </button>
          );
        })}
      </div>

      {!isPro && (
        <div className="mb-4 rounded-lg border border-border bg-surface px-3 py-2 text-xs text-neutral-500">
          Upgrade to Pro to unlock sorting and full post history.
          <a
            href="/dashboard/billing"
            className="ml-2 font-medium text-black hover:underline"
          >
            Upgrade
          </a>
        </div>
      )}

      {/* Table */}
      {isError && (
        <div className="mb-4 rounded-lg border border-danger/20 bg-red-50 p-3 text-sm text-danger">
          We couldn’t load your posts. Please try again shortly.
        </div>
      )}
      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-16 animate-pulse rounded-lg bg-surface" />
          ))}
        </div>
      ) : data && data.posts.length > 0 ? (
        <>
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-surface">
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-neutral-400">
                    Post
                  </th>
                  <th className="px-3 py-3 text-right text-xs font-medium uppercase tracking-wide text-neutral-400">
                    Views
                  </th>
                  <th className="px-3 py-3 text-right text-xs font-medium uppercase tracking-wide text-neutral-400">
                    Likes
                  </th>
                  <th className="px-3 py-3 text-right text-xs font-medium uppercase tracking-wide text-neutral-400">
                    Replies
                  </th>
                  <th className="px-3 py-3 text-right text-xs font-medium uppercase tracking-wide text-neutral-400">
                    Reposts
                  </th>
                  <th className="px-3 py-3 text-right text-xs font-medium uppercase tracking-wide text-neutral-400 hidden sm:table-cell">
                    Shares
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.posts.map((post) => (
                  <tr
                    key={post.id}
                    className="border-b border-border last:border-0 hover:bg-surface-hover transition-colors"
                  >
                    <td className="px-4 py-3 max-w-xs">
                      <div className="flex items-start gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="truncate text-sm">
                            {post.text || (
                              <span className="italic text-neutral-400">
                                [{post.mediaType?.toLowerCase() || "media"}]
                              </span>
                            )}
                          </p>
                          <p className="text-xs text-neutral-400 mt-0.5">
                            {timeAgo(post.publishedAt)}
                          </p>
                        </div>
                        {post.permalink && (
                          <a
                            href={post.permalink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="shrink-0 text-neutral-300 hover:text-black"
                          >
                            <ExternalLink className="h-3.5 w-3.5" />
                          </a>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums">{post.views.toLocaleString()}</td>
                    <td className="px-3 py-3 text-right tabular-nums">{post.likes.toLocaleString()}</td>
                    <td className="px-3 py-3 text-right tabular-nums">{post.replies.toLocaleString()}</td>
                    <td className="px-3 py-3 text-right tabular-nums">{post.reposts.toLocaleString()}</td>
                    <td className="px-3 py-3 text-right tabular-nums hidden sm:table-cell">{post.shares.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {isPro ? (
            <div className="mt-4 flex items-center justify-between text-sm">
              <span className="text-neutral-400">
                {data.total} posts total
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page <= 1}
                  className="flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-sm hover:bg-surface-hover disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Prev
                </button>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page * data.limit >= data.total}
                  className="flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-sm hover:bg-surface-hover disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          ) : (
            <div className="mt-3 text-xs text-neutral-400">
              Showing your 5 most recent posts.
            </div>
          )}
        </>
      ) : (
        <div className="rounded-xl border border-border bg-surface p-8 text-center text-sm text-neutral-400">
          No posts found for this period.
        </div>
      )}
    </div>
  );
}
